// Google Identity Services (GIS) wrapper para o token implícito.
// O token nunca vai pro localStorage — só sessionStorage, então fechar a aba
// já obriga novo login.

const GIS_SCRIPT_URL = "https://accounts.google.com/gsi/client";
// `drive.readonly` basta: a app só lê HTMLs já existentes no Drive do usuário.
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.readonly";

const CLIENT_ID_STORAGE_KEY = "drive-viewer.clientId";
const TOKEN_STORAGE_KEY = "drive-viewer.token";

const ENV_CLIENT_ID =
  typeof import.meta.env.VITE_DRIVE_CLIENT_ID === "string"
    ? import.meta.env.VITE_DRIVE_CLIENT_ID.trim()
    : "";

type GisTokenResponse = {
  access_token?: string;
  expires_in?: number;
  scope?: string;
  error?: string;
};

type GisTokenClient = {
  requestAccessToken: (overrides?: { prompt?: string }) => void;
  callback: (response: GisTokenResponse) => void;
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GisTokenResponse) => void;
          }) => GisTokenClient;
          revoke: (token: string, done: () => void) => void;
        };
      };
    };
  }
}

let tokenClient: GisTokenClient | null = null;
let accessToken: string | null = null;
let tokenExpiresAt = 0;
let scriptPromise: Promise<void> | null = null;

const listeners = new Set<(connected: boolean) => void>();

type StoredToken = { access_token: string; expires_at: number };

function persistToken(): void {
  if (typeof window === "undefined") return;
  if (!accessToken) {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    return;
  }
  const payload: StoredToken = { access_token: accessToken, expires_at: tokenExpiresAt };
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage indisponível (modo privado etc) — reload pede login de novo.
  }
}

function restoreTokenFromStorage(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Partial<StoredToken>;
    if (
      typeof parsed.access_token === "string" &&
      typeof parsed.expires_at === "number" &&
      parsed.expires_at > Date.now() + 30_000
    ) {
      accessToken = parsed.access_token;
      tokenExpiresAt = parsed.expires_at;
    } else {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // ignore — JSON corrompido.
  }
}

restoreTokenFromStorage();

export function getStoredClientId(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(CLIENT_ID_STORAGE_KEY);
}

export function setStoredClientId(value: string | null): void {
  if (typeof window === "undefined") return;
  if (value && value.trim().length > 0) {
    window.localStorage.setItem(CLIENT_ID_STORAGE_KEY, value.trim());
  } else {
    window.localStorage.removeItem(CLIENT_ID_STORAGE_KEY);
  }
}

export function isClientIdProvisioned(): boolean {
  return ENV_CLIENT_ID.length > 0;
}

export function getEffectiveClientId(): string | null {
  if (ENV_CLIENT_ID.length > 0) return ENV_CLIENT_ID;
  return getStoredClientId();
}

export function isConnected(): boolean {
  return accessToken !== null && Date.now() < tokenExpiresAt;
}

export function onAuthChange(handler: (connected: boolean) => void): () => void {
  listeners.add(handler);
  return () => {
    listeners.delete(handler);
  };
}

function notifyAuthChange() {
  for (const listener of listeners) {
    try {
      listener(isConnected());
    } catch (error) {
      console.error("[drive-viewer] auth listener threw", error);
    }
  }
}

async function ensureGisLoaded(): Promise<void> {
  if (window.google?.accounts?.oauth2) return;
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${GIS_SCRIPT_URL}"]`,
      );
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener(
          "error",
          () => reject(new Error("Falha ao carregar Google Identity Services.")),
          { once: true },
        );
        return;
      }
      const script = document.createElement("script");
      script.src = GIS_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", () => resolve(), { once: true });
      script.addEventListener(
        "error",
        () => reject(new Error("Falha ao carregar Google Identity Services.")),
        { once: true },
      );
      document.head.appendChild(script);
    });
  }
  await scriptPromise;
}

function buildTokenClient(clientId: string): GisTokenClient {
  if (!window.google?.accounts?.oauth2) {
    throw new Error("Google Identity Services não inicializou.");
  }
  return window.google.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: DRIVE_SCOPE,
    callback: () => {
      // Substituído por requisição em connect()/refresh().
    },
  });
}

/** Dispara popup OAuth. Precisa rodar a partir de gesto do usuário. */
export async function connect(clientId?: string): Promise<void> {
  const provided = clientId?.trim() ?? "";
  const effective = provided.length > 0 ? provided : (getEffectiveClientId() ?? "");
  if (!effective) throw new Error("Client ID do Google não está configurado.");
  if (!isClientIdProvisioned() && provided.length > 0) {
    setStoredClientId(provided);
  }
  await ensureGisLoaded();
  tokenClient = buildTokenClient(effective);

  await new Promise<void>((resolve, reject) => {
    if (!tokenClient) {
      reject(new Error("Token client não pronto."));
      return;
    }
    tokenClient.callback = (response) => {
      if (response.error) {
        reject(new Error(response.error));
        return;
      }
      if (!response.access_token) {
        reject(new Error("Drive devolveu token vazio."));
        return;
      }
      accessToken = response.access_token;
      tokenExpiresAt = Date.now() + Math.max(60_000, ((response.expires_in ?? 3600) - 60) * 1000);
      persistToken();
      notifyAuthChange();
      resolve();
    };
    tokenClient.requestAccessToken({ prompt: "consent" });
  });
}

/** Refresh silencioso — chamar enquanto a cadeia de gesto do usuário ainda está ativa. */
export async function refresh(): Promise<void> {
  if (!tokenClient) {
    const clientId = getEffectiveClientId();
    if (!clientId) throw new Error("Drive não está conectado.");
    await ensureGisLoaded();
    tokenClient = buildTokenClient(clientId);
  }
  await new Promise<void>((resolve, reject) => {
    if (!tokenClient) {
      reject(new Error("Token client não pronto."));
      return;
    }
    tokenClient.callback = (response) => {
      if (response.error) {
        reject(new Error(response.error));
        return;
      }
      if (!response.access_token) {
        reject(new Error("Drive devolveu token vazio."));
        return;
      }
      accessToken = response.access_token;
      tokenExpiresAt = Date.now() + Math.max(60_000, ((response.expires_in ?? 3600) - 60) * 1000);
      persistToken();
      notifyAuthChange();
      resolve();
    };
    tokenClient.requestAccessToken({ prompt: "" });
  });
}

export async function getAccessToken(): Promise<string> {
  if (!isConnected()) {
    await refresh();
  }
  if (!accessToken) throw new Error("Drive não está conectado.");
  return accessToken;
}

export async function disconnect(): Promise<void> {
  const token = accessToken;
  accessToken = null;
  tokenExpiresAt = 0;
  tokenClient = null;
  persistToken();
  notifyAuthChange();
  if (token && window.google?.accounts?.oauth2?.revoke) {
    await new Promise<void>((resolve) => {
      window.google!.accounts!.oauth2!.revoke(token, () => resolve());
    });
  }
}
